import React, { useState, useEffect, useRef } from 'react'
import { ChevronDown, Search } from 'lucide-react'
import { useController, UseControllerProps } from 'react-hook-form'

import { useQuery, useQueryClient } from '@tanstack/react-query'

import { Button } from '@/components/ui/button'
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { useDebounce } from '@/hooks/useDebounce'

export interface AsyncSelectOption {
  id: string | number
  label: string
  subtitle?: string
}

interface DefaultItem {
  id: string | number
  name: string
  description?: string
  observations?: string
  code?: string
}

interface AsyncSelectFormProps extends UseControllerProps {
  label?: string
  fetcher: (_search: string) => Promise<AsyncSelectOption[]>
  placeholder?: string
  multiple?: boolean
  notFoundMessage?: string
  disabled?: boolean
  defaultValue?: DefaultItem | DefaultItem[] | string | number | null
  /**
   * Cuando es true NO inyecta el defaultValue en el cache local para la búsqueda vacía.
   * Útil para casos (categorías de contenedores) donde queremos forzar el primer fetch
   * siempre desde backend y evitar que sólo aparezcan las categorías previamente seleccionadas.
   */
  disableDefaultValueCaching?: boolean
}

const AsyncSelectForm = React.forwardRef<
  HTMLButtonElement,
  AsyncSelectFormProps
>(
  (
    {
      name,
      control,
      label,
      fetcher,
      placeholder = 'Buscar...',
      multiple = false,
      notFoundMessage,
      disabled = false,
      defaultValue,
      disableDefaultValueCaching = false,
      ...rest
    },
    ref
  ) => {
    const { field, fieldState } = useController({ name, control, ...rest })
    const [isPopoverOpen, setIsPopoverOpen] = useState(false)
    const [searchQuery, setSearchQuery] = useState('')
    const debouncedSearch = useDebounce(searchQuery, 400)
    const queryClient = useQueryClient()

    // --- CACHE LOCAL DE BÚSQUEDAS ---
    // Clave: `${name}:${debouncedSearch}`
    const searchCacheRef = useRef<Map<string, AsyncSelectOption[]>>(
      new Map<string, AsyncSelectOption[]>()
    )

    // ---
    const fetchWithCache = async (
      search: string
    ): Promise<AsyncSelectOption[]> => {
      const cacheKey = `${name}:${search}`
      const cached = searchCacheRef.current.get(cacheKey)
      if (cached) {
        return cached
      }
      const result = await fetcher(search)
      searchCacheRef.current.set(cacheKey, result)
      return result
    }

    const { data: fetchedOptions = [], isLoading } = useQuery<
      AsyncSelectOption[]
    >({
      queryKey: [name, debouncedSearch],
      queryFn: () => fetchWithCache(debouncedSearch),
      staleTime: 30 * 60 * 1000, // 30 minutos
    })

    // Crear opciones a partir de defaultValue para mostrar valores seleccionados
    const defaultValueOptions = React.useMemo(() => {
      if (!defaultValue) return []

      if (multiple && Array.isArray(defaultValue)) {
        return defaultValue.map((item) => ({
          id: item.id,
          label: item.name,
          subtitle: item.description || item.observations,
        }))
      } else if (
        !multiple &&
        typeof defaultValue === 'object' &&
        !Array.isArray(defaultValue) &&
        defaultValue.id &&
        defaultValue.name
      ) {
        return [
          {
            id: defaultValue.id,
            label: defaultValue.name,
            subtitle:
              defaultValue.description ||
              defaultValue.observations ||
              defaultValue.code,
          },
        ]
      }
      return []
    }, [defaultValue, multiple])
    // Combinar opciones del backend con las seleccionadas (sin duplicados)
    const options = React.useMemo(() => {
      if (disableDefaultValueCaching) {
        const filteredFetched = fetchedOptions.filter(
          (opt) =>
            !defaultValueOptions.some(
              (defOpt) => defOpt.id.toString() === opt.id.toString()
            )
        )
        return [...defaultValueOptions, ...filteredFetched]
      } else {
        const combinedOptions = [...fetchedOptions]
        defaultValueOptions.forEach((defaultOption) => {
          if (
            !combinedOptions.some(
              (opt) => opt.id.toString() === defaultOption.id.toString()
            )
          ) {
            combinedOptions.unshift(defaultOption)
          }
        })
        return combinedOptions
      }
    }, [fetchedOptions, defaultValueOptions, disableDefaultValueCaching])

    // --- Manejar defaultValue inicial para el caché (solo si no está deshabilitado) ---
    useEffect(() => {
      if (defaultValue && !disableDefaultValueCaching) {
        // Para múltiple (categorías)
        if (multiple && Array.isArray(defaultValue)) {
          const defaultOptions: AsyncSelectOption[] = defaultValue.map(
            (item) => ({
              id: item.id,
              label: item.name,
              subtitle: item.description || item.observations,
            })
          )

          // Agregar al cache para búsqueda vacía
          const cacheKey = `${name}:`
          const currentOptions = searchCacheRef.current.get(cacheKey) || []
          const newOptions = [...currentOptions]

          defaultOptions.forEach((defaultOption) => {
            if (!newOptions.some((opt) => opt.id === defaultOption.id)) {
              newOptions.unshift(defaultOption)
            }
          })

          searchCacheRef.current.set(cacheKey, newOptions)

          // Invalidar query para mostrar las opciones inmediatamente
          queryClient.invalidateQueries({ queryKey: [name, debouncedSearch] })
        }
        // Para único (país)
        else if (
          !multiple &&
          typeof defaultValue === 'object' &&
          !Array.isArray(defaultValue) &&
          defaultValue.id &&
          defaultValue.name
        ) {
          const defaultOption: AsyncSelectOption = {
            id: defaultValue.id,
            label: defaultValue.name,
            subtitle:
              defaultValue.description ||
              defaultValue.observations ||
              defaultValue.code,
          }

          // Agregar al cache para búsqueda vacía
          const cacheKey = `${name}:`
          const currentOptions = searchCacheRef.current.get(cacheKey) || []
          if (!currentOptions.some((opt) => opt.id === defaultOption.id)) {
            searchCacheRef.current.set(cacheKey, [
              defaultOption,
              ...currentOptions,
            ])
            // Invalidar query para mostrar la opción inmediatamente
            queryClient.invalidateQueries({ queryKey: [name, debouncedSearch] })
          }
        }
      }
    }, [
      defaultValue,
      name,
      debouncedSearch,
      queryClient,
      multiple,
      disableDefaultValueCaching,
    ])

    // Para selección simple
    const selected = !multiple
      ? (() => {
          if (field.value === null || field.value === undefined) {
            return undefined
          }
          if (
            typeof field.value === 'object' &&
            field.value.id &&
            field.value.label
          ) {
            return field.value as AsyncSelectOption
          }
          const valueToCompare =
            typeof field.value === 'object' && field.value !== null
              ? field.value.id
              : field.value
          return (options as AsyncSelectOption[])
            .filter((opt) => opt !== null && opt !== undefined)
            .find((opt) => opt.id === valueToCompare)
        })()
      : Array.isArray(field.value)
        ? ((): AsyncSelectOption[] => {
            const currentFieldValues = field.value as unknown[]
            const fieldIds = currentFieldValues
              .map((item) => {
                if (item === null || item === undefined) return null
                if (typeof item === 'object' && 'id' in (item as object)) {
                  return (item as { id: string | number }).id
                }
                return item as string | number
              })
              .filter(
                (id): id is string | number => id !== null && id !== undefined
              )
            return (options as AsyncSelectOption[]).filter((opt) =>
              fieldIds.includes(opt.id)
            )
          })()
        : []

    const handleSelect = (option: AsyncSelectOption) => {
      if (multiple) {
        let updatedIds: (string | number)[] = Array.isArray(field.value)
          ? (field.value as unknown[])
              .map((item) => {
                if (item === null || item === undefined) return null
                if (typeof item === 'object' && 'id' in (item as object)) {
                  return (item as { id: string | number }).id
                }
                return item as string | number
              })
              .filter(
                (id): id is string | number => id !== null && id !== undefined
              )
          : []

        if (updatedIds.includes(option.id)) {
          updatedIds = updatedIds.filter((id) => id !== option.id)
        } else {
          updatedIds.push(option.id)
        }
        field.onChange(updatedIds)
      } else {
        field.onChange(option.id)
        setIsPopoverOpen(false)
      }
    }

    const isSelected = (option: AsyncSelectOption) => {
      if (multiple) {
        return (
          Array.isArray(field.value) &&
          (field.value as unknown[]).some((item) => {
            const id =
              typeof item === 'object' &&
              item !== null &&
              'id' in (item as object)
                ? (item as { id: string | number }).id
                : (item as string | number)
            return id === option.id
          })
        )
      }
      return field.value === option.id
    }

    return (
      <div className={'w-full'}>
        <FormField
          control={control}
          name={name}
          render={() => (
            <FormItem>
              {label && <FormLabel>{label}</FormLabel>}
              <FormControl>
                <Popover
                  modal={false}
                  onOpenChange={setIsPopoverOpen}
                  open={isPopoverOpen}
                >
                  <PopoverTrigger asChild>
                    <Button
                      aria-expanded={isPopoverOpen}
                      className="h-fit w-full justify-between font-normal"
                      disabled={disabled}
                      ref={ref}
                      role="combobox"
                      variant="outline"
                    >
                      {multiple ? (
                        <div className="flex flex-wrap gap-1">
                          {Array.isArray(selected) && selected.length > 0
                            ? selected.map((opt: AsyncSelectOption) => (
                                <span
                                  className="rounded bg-muted px-2 py-0.5 text-xs"
                                  key={opt.id}
                                >
                                  {opt.label}
                                </span>
                              ))
                            : 'Seleccionar...'}
                        </div>
                      ) : selected ? (
                        <span>{(selected as AsyncSelectOption).label}</span>
                      ) : (
                        'Seleccionar...'
                      )}
                      <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    align="start"
                    avoidCollisions={true}
                    className="w-[--radix-popover-trigger-width] p-0"
                    collisionPadding={8}
                    side="bottom"
                    sideOffset={4}
                    style={{ zIndex: 9999 }}
                  >
                    <div className="px-3 pt-3">
                      <div className="relative mb-2">
                        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                        <Input
                          autoFocus
                          className="pl-9"
                          onChange={(e) => setSearchQuery(e.target.value)}
                          placeholder={placeholder}
                          value={searchQuery}
                        />
                      </div>
                    </div>
                    <div
                      className="max-h-[200px] touch-pan-y overflow-y-auto"
                      onTouchMove={(event) => event.stopPropagation()}
                      onTouchStart={(event) => event.stopPropagation()}
                      onWheel={(e) => {
                        const target = e.currentTarget
                        target.scrollTop += e.deltaY
                      }}
                      style={{
                        WebkitOverflowScrolling: 'touch',
                        overscrollBehavior: 'contain',
                      }}
                    >
                      {isLoading ? (
                        <div className="p-4 text-center text-sm text-muted-foreground">
                          Cargando...
                        </div>
                      ) : (options as AsyncSelectOption[]).length > 0 ? (
                        (options as AsyncSelectOption[]).map((option) => (
                          <div
                            className={
                              'flex cursor-pointer items-center gap-2 p-2 hover:bg-muted ' +
                              (isSelected(option) ? 'bg-accent' : '')
                            }
                            key={option.id}
                            onClick={() => handleSelect(option)}
                          >
                            <span className="font-medium">{option.label}</span>
                            {isSelected(option) && (
                              <span className="ml-auto text-xs">✓</span>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="p-4 text-center text-sm text-muted-foreground">
                          {notFoundMessage ?? 'No se encontraron resultados.'}
                        </div>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
              </FormControl>
              <FormMessage>{fieldState.error?.message}</FormMessage>
            </FormItem>
          )}
        />
      </div>
    )
  }
)
AsyncSelectForm.displayName = 'AsyncSelectForm'
export { AsyncSelectForm }
